import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Stack,
} from '@mui/material';
import { Refresh, BugReport } from '@mui/icons-material';

interface ErrorFallbackProps {
  error?: Error;
  resetError?: () => void;
  title?: string;
  message?: string;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError,
  title = 'Something went wrong',
  message = 'An unexpected error occurred. Please try refreshing the page.',
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        p: 3,
      }}
    >
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardContent>
          <Stack spacing={3} alignItems="center" textAlign="center">
            <BugReport
              sx={{
                fontSize: 64,
                color: 'error.main',
                opacity: 0.7,
              }}
            />
            
            <Typography variant="h5" component="h2" fontWeight="bold">
              {title}
            </Typography>
            
            <Typography variant="body1" color="text.secondary">
              {message}
            </Typography>
            
            {error && import.meta.env.MODE === 'development' && (
              <Alert severity="error" sx={{ width: '100%', textAlign: 'left' }}>
                <Typography variant="body2" component="pre" sx={{ fontSize: '0.8rem' }}>
                  {error.message}
                  {error.stack && (
                    <>
                      <br />
                      <br />
                      Stack trace:
                      <br />
                      {error.stack.split('\n').slice(0, 5).join('\n')}
                    </>
                  )}
                </Typography>
              </Alert>
            )}
            
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={() => {
                  if (resetError) {
                    resetError();
                  } else {
                    window.location.reload();
                  }
                }}
              >
                Try Again
              </Button>
              
              <Button
                variant="outlined"
                onClick={() => window.location.href = '/dashboard'}
              >
                Go to Dashboard
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ErrorFallback;